import { Target } from "lucide-react";
import { SG7TacticalPanel, OddPill } from "./sg7";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import type { SafeNumbers, N8NPick } from "@/hooks/useAnalysis";

interface PicksCardProps {
  picks?: N8NPick[] | null;
  safeNumbers?: SafeNumbers | null;
  homeTeamName?: string;
  awayTeamName?: string;
  className?: string;
}

export function PicksCard({ 
  picks, 
  safeNumbers,
  homeTeamName,
  awayTeamName,
  className 
}: PicksCardProps) {
  const { t } = useTranslation('analysis');
  
  // Use translated defaults if team names not provided
  const home = homeTeamName || t('defaults.home');
  const away = awayTeamName || t('defaults.away');
  
  // PicksCard now only uses safeNumbers for favorite display
  // Technical picks are handled by TechnicalPicksCard
  const odds = safeNumbers?.odds_ft_1x2;

  if (!odds) return null;

  const getSelectionLabel = (selection: string) => {
    if (selection === 'home') return home;
    if (selection === 'away') return away;
    return t('cards.draw');
  };


  return (
    <SG7TacticalPanel className={className}>
      <div className="p-4">
        <h3 className="text-sm font-bold flex items-center gap-2 text-[hsl(var(--sg7-text))] mb-3">
          <Target className="h-4 w-4 text-[hsl(var(--sg7-gold))]" />
          {t('cards.favorite')}
        </h3>
        {odds?.favorite ? (
          (() => {
            const favoriteOdd = odds.favorite.selection === 'home' ? odds.home
              : odds.favorite.selection === 'away' ? odds.away
              : odds.draw;
            
            return (
              <div className="flex items-center justify-between p-3 bg-[hsl(var(--sg7-surface-2))] rounded border border-[hsl(var(--sg7-gold)/0.3)]">
                <span className="text-sm font-bold text-[hsl(var(--sg7-gold))]">
                  {getSelectionLabel(odds.favorite.selection)}
                </span>
                <OddPill odds={favoriteOdd} size="md" variant="gold" />
              </div>
            );
          })()
        ) : odds ? (
          <div className="flex items-center justify-around p-3 bg-[hsl(var(--sg7-surface-2))] rounded">
            <div className="text-center">
              <span className="text-xs text-[hsl(var(--sg7-text-muted))]">1</span>
              <p className="text-sm font-bold text-[hsl(var(--sg7-text))]">{odds.home?.toFixed(2) || '-'}</p>
            </div>
            <div className="text-center">
              <span className="text-xs text-[hsl(var(--sg7-text-muted))]">X</span>
              <p className="text-sm font-bold text-[hsl(var(--sg7-text))]">{odds.draw?.toFixed(2) || '-'}</p>
            </div>
            <div className="text-center">
              <span className="text-xs text-[hsl(var(--sg7-text-muted))]">2</span>
              <p className="text-sm font-bold text-[hsl(var(--sg7-text))]">{odds.away?.toFixed(2) || '-'}</p>
            </div>
          </div>
        ) : null}
      </div>
    </SG7TacticalPanel>
  );
}
